import React, { useState } from 'react';
import { Box, Typography, Button, Stepper, Step, StepLabel, Card, CardContent, Switch, FormControlLabel, TextField, CircularProgress, Alert } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpactAnalysisPanel } from './ImpactAnalysisPanel';
import { fetchApi } from '@/api/fetchApi';

export interface GrantEntitlementWizardProps {
  tenantId: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const steps = ['Select Domain', 'Impact Analysis', 'Options & Confirm'];

export function GrantEntitlementWizard({ tenantId, onComplete, onCancel }: GrantEntitlementWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(30);
  const [expiryDate, setExpiryDate] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const queryClient = useQueryClient();

  const { data: availableDomains, isLoading: loadingDomains } = useQuery({
    queryKey: ['available-domains', tenantId],
    queryFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements/available`),
    staleTime: 5 * 60 * 1000,
  });

  const { data: domains } = useQuery({
    queryKey: ['domains'],
    queryFn: () => fetchApi('/domains'),
    staleTime: 5 * 60 * 1000,
  });

  const grantMutation = useMutation({
    mutationFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements/grant`, {
      method: 'POST',
      body: JSON.stringify({
        domainId: selectedDomain,
        status: isTrial ? 'Trial' : 'Active',
        trialDays: isTrial ? trialDays : 0,
        expiresAt: expiryDate || null,
        paid: isPaid,
      }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entitlements', tenantId] });
      onComplete?.();
    },
  });

  const domainList = (Array.isArray(domains) ? domains : []) as Array<{ domainId: string; name: string; description: string; billingTier: string; entities?: string[] }>;
  const available = (Array.isArray(availableDomains) ? availableDomains : []) as string[];
  const availableDomainData = domainList.filter((d) => available.includes(d.domainId));
  const selectedDomainData = domainList.find((d) => d.domainId === selectedDomain);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      grantMutation.mutate();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Grant Entitlement</Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>Select a domain to enable:</Typography>
          {loadingDomains ? (
            <CircularProgress size={24} />
          ) : availableDomainData.length === 0 ? (
            <Alert severity="info">No domains available to grant.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {availableDomainData.map((d) => (
                <Card
                  key={d.domainId}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    bgcolor: selectedDomain === d.domainId ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => setSelectedDomain(d.domainId)}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.name} ({d.domainId})</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.billingTier} tier • {d.entities?.length || 0} entities
                    </Typography>
                    {d.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{d.description}</Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {activeStep === 1 && selectedDomain && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>Impact analysis for enabling <strong>{selectedDomain}</strong>:</Typography>
          <ImpactAnalysisPanel domainId={selectedDomain} action="enable" tenantId={tenantId} />
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Granting: {selectedDomainData?.name || selectedDomain}
          </Typography>

          <FormControlLabel
            control={<Switch checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />}
            label="Trial entitlement"
          />

          {isTrial && (
            <TextField
              label="Trial days"
              type="number"
              value={trialDays}
              onChange={(e) => setTrialDays(Number(e.target.value))}
              size="small"
              sx={{ maxWidth: 200 }}
            />
          )}

          <TextField
            label="Expiry date (optional)"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: 250 }}
          />

          <FormControlLabel
            control={<Switch checked={isPaid} onChange={(e) => setIsPaid(e.target.checked)} />}
            label="Paid entitlement"
          />

          {grantMutation.isError && <Alert severity="error">Grant failed. Please try again.</Alert>}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            (activeStep === 0 && !selectedDomain) ||
            grantMutation.isPending
          }
        >
          {activeStep === steps.length - 1 ? 'Grant Entitlement' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}
